import useAuth from '@app/auth/use-auth';
import { LabelDownloadModal } from '@app/components/label-download-modal';
import { pcfRoutes } from '@app/pcf/pcf-routes';
import { serializeProductPdfParams } from '@app/pcf/product/product-detail/pdf/product-pdf-params';
import { useAnalytics } from '@app/services/analytics/AnalyticsContext';
import { OrganizationStatusEnum, ProductResult } from '@shared/api/types';
import { Icons } from '@shared/components/content/icons';
import Text from '@shared/components/content/text';
import RatingLabel from '@shared/components/ratings/rating-label';
import { Button } from '@shared/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@shared/components/ui/dropdown-menu';
import { cn } from '@shared/lib/utils';
import React, { useState } from 'react';
import { generatePath } from 'react-router-dom';

interface ProductDetailCommunicateResultsProps
  extends React.HTMLAttributes<HTMLDivElement> {
  productResult: ProductResult;
}

export function ProductDetailCommunicateResults({
  productResult,
  className,
}: ProductDetailCommunicateResultsProps) {
  const { user } = useAuth();
  const { track } = useAnalytics();
  const [labelModalOpen, setLabelModalOpen] = useState(false);

  const handleDownloadPdf = (withBreakdown: boolean) => {
    track('Downloaded Product PDF', {
      product_uuid: productResult.uuid,
      pdf_variant: withBreakdown ? 'full_breakdown' : 'summary',
    });
    const path = generatePath(pcfRoutes.PRODUCT_PDF_DOWNLOAD, {
      product_id: productResult.uuid,
    });
    window.open(
      serializeProductPdfParams(path, { breakdown: withBreakdown }),
      '_blank'
    );
  };

  return (
    <div className={cn('flex w-full flex-col space-y-4', className)}>
      <Text variant="cardTitle" data-testid="communicate-results-title">
        Communicate your results
      </Text>
      <div className="flex gap-2">
        <RatingLabel
          rating={productResult.emissionsCategory!}
          className="w-16"
        />
        <div className="pt-1">
          <Text variant="subtle">
            Add your carbon labels to menus, packaging and other promotional
            materials.
          </Text>
          <div className="mt-2 flex flex-wrap gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="secondary"
                  className="border-teal-600 text-teal-600"
                  disabled={
                    user?.organizationStatus === OrganizationStatusEnum.trial
                  }
                  data-testid="download-pdf-button"
                >
                  Download PDF
                  <Icons.chevronDown className="ml-1 size-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                <DropdownMenuItem onClick={() => handleDownloadPdf(false)}>
                  <Icons.fileChartColumn className="mr-2 size-4" />
                  Summary
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleDownloadPdf(true)}>
                  <Icons.list className="mr-2 size-4" />
                  Full breakdown
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button
              variant="secondary"
              className="border-teal-600 bg-teal-600 text-gray-00 hover:bg-teal-700"
              onClick={() => setLabelModalOpen(true)}
            >
              Download label
            </Button>
          </div>
        </div>
      </div>

      {productResult.emissionsCategory && (
        <LabelDownloadModal
          open={labelModalOpen}
          onClose={() => setLabelModalOpen(false)}
          defaultRating={productResult.emissionsCategory}
        />
      )}
    </div>
  );
}
